import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { Slide, styled } from "@mui/material";
import { DiseaseCategoryType } from "fe-common/graphql";
import { useSuggestionsForPlan } from "../hooks/useSuggestionsForPlan";
import { useDiseasesSections } from "../hooks/useDiseasesSections";
import { PatientCaseChange, usePatientCase } from "../usePatientCase";

import { scrollableStyle } from "fe-common/ui/commonStyles";
import { useGetDiseasesInPlan } from "../hooks/useGetDiseasesInPlan";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { PlanTabs } from "../recommendedPlan/planTabs";
import { useBuildPlanPanels } from "../useBuildPlanPanels";
import { PlanClusters } from "../recommendedPlan/planClusters";
//region [[ Styles ]]

const LabWorkupListView = styled("div")({
  ...scrollableStyle,
  padding: "0px",
});

const NoSuggestionsText = styled("div")({
  display: "flex",
  width: "100%",
  height: "100%",
  flexFlow: "column",
  justifyContent: "center",
  textAlign: "center",
  color: KahunColorMap.DefaultLight,
});

const PLAN_BOX_PADDING = 37;
const WorkupList = styled("div")(
  ({ selectedtab, loading }: { selectedtab: string; loading: string }) => ({
    borderTopRightRadius: selectedtab === "left" ? "50px" : "0px",
    borderTopLeftRadius: selectedtab === "right" ? "50px" : "0px",
    paddingTop: "30px",
    height: loading ? "100%" : "unset",
    backgroundColor: KahunColorMap.AccentLight,
    paddingLeft: `${PLAN_BOX_PADDING}px`,
    paddingRight: `${PLAN_BOX_PADDING}px`,
    paddingBottom: "40px",
  }),
);

//endregion [[ Styles ]]

//region [[ Props ]]

export interface LabWorkupListProps {
  className?: string;
  /***
   * Get notified on each plan item update
   */
  onChange?: () => void;
  /***
   * If set to true by default every change will update the patient case.
   * if left empty or set to false pandingPlanChanges will be marked
   * with the change
   */
  updateCaseOnClick?: boolean;
  /***
   * How many items can we allow in this list?
   */
  maxItems?: number;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const LabWorkupList = ({
  onChange,
  updateCaseOnClick,
  maxItems,
  className,
  ...props
}: LabWorkupListProps) => {
  let {
    findingSuggestions: nextStepSuggestions,
    planIsLoading,
    clusteredSuggestions,
  } = useSuggestionsForPlan();

  const { doChangesToPending, doChangesToPatientCase } = usePatientCase();

  const [categoryType, setCategoryType] = useState(
    DiseaseCategoryType.OutcomeTest,
  );

  const { loading: ddIsLoading } = useDiseasesSections();

  const diseasesInPlan = useGetDiseasesInPlan();

  const onlyOutcomeSuggestions = useMemo(
    () =>
      nextStepSuggestions.filter(
        (suggestion) => suggestion.category === categoryType,
      ),
    [categoryType, nextStepSuggestions],
  );

  const {
    labSuggestionsPanels: labsPanels,
    loading: loadingPanels,
  } = useBuildPlanPanels(
    onlyOutcomeSuggestions,
    clusteredSuggestions,
    categoryType,
  );

  const loading = ddIsLoading || planIsLoading || loadingPanels;

  const onPlanItemChange = useCallback(
    (change: PatientCaseChange) => {
      if (updateCaseOnClick) {
        doChangesToPatientCase([change]);
      } else {
        doChangesToPending([change]);
      }
      if (onChange) onChange();
    },
    [updateCaseOnClick, doChangesToPatientCase, doChangesToPending, onChange],
  );

  const noResults = labsPanels.length === 0;

  const setLabs = () => {
    setCategoryType(DiseaseCategoryType.OutcomeTest);
  };

  const setImaging = () => {
    setCategoryType(DiseaseCategoryType.Imaging);
  };

  const noItemsText = diseasesInPlan?.length ? (
    <NoSuggestionsText>
      Oops... something went wrong. Please try reloading the page. If the
      problem persist, please contact
      <a href="mailto://support@kahun.com">support@kahun.com</a>
    </NoSuggestionsText>
  ) : (
    <div></div>
  );

  return (
    <LabWorkupListView className={className}>
      {!loading && noResults && (
        <Slide direction="up" in mountOnEnter unmountOnExit>
          {noItemsText}
        </Slide>
      )}

      <PlanTabs
        labsClicked={setLabs}
        imagingClicked={setImaging}
        selected={categoryType}
      />

      <WorkupList
        loading={loading.toString()}
        selectedtab={
          categoryType === DiseaseCategoryType.OutcomeTest ? "left" : "right"
        }
      >
        <PlanClusters
          loading={loading}
          onPlanItemChange={onPlanItemChange}
          clusters={labsPanels}
          maxItems={maxItems}
        />
      </WorkupList>
    </LabWorkupListView>
  );
};
